import React, {
  Dispatch,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  ChangeEvent,
} from 'react';
import Flatpickr from 'react-flatpickr';
import { ResponsiveBar, Layer } from '@nivo/bar';
import {
  GraphData,
  OrderDateByAccountCount,
  PartnersNameWithGraphColor,
  VolumeSerializedValues,
  VolumesReportInterface,
} from './VolumesReport.types';
import dayjs from 'dayjs';
import { kelly_colors } from '../../../const/color';
import { Loader } from '../../../components/Loader/Loader';
import { ComboBoxComponent } from '../../../ReusableComponents/ComboBox/ComboBoxComponent';
import { ComboBoxItem } from '../../../ReusableComponents/ComboBox/ComboBoxComponent.types';
import { invertColor } from '../../oldPlanner/utils/bundleDraft';
import { CheckboxComponent } from '../../../ReusableComponents/Checkbox/CheckboxComponent';
import { TabsComponent } from '../../../ReusableComponents/Tabs/TabsComponent';
import './VolumesReport.scss';
import { sortRegionsForView } from '../../../utils/reportUtils';
import { Helmet } from 'react-helmet';
import { fetchDeliveriesByAccount } from '../../../fetch/fetchStats';

function doCheckSelectedPeriod(selectedDates: Date[], instance: any): void {
  if (selectedDates != null && selectedDates.length === 1) {
    const startDate = dayjs(selectedDates[0]);
    const maxDate = startDate.add(30, 'day');
    const minDate = startDate.subtract(30, 'day');
    instance.set('minDate', minDate.toISOString());
    instance.set('maxDate', maxDate.toISOString());
    instance.jumpToDate(minDate.toISOString(), true);
  }
}

function filterPartnerColorBySelected(
  sourceData: GraphData | PartnersNameWithGraphColor,
  selectedValues: string[]
): PartnersNameWithGraphColor {
  return Object.keys(sourceData)
    .filter(key => selectedValues.includes(key))
    .reduce((obj, key) => {
      obj[key] = sourceData[key];
      return obj;
    }, {});
}

function filterPartnerBySelected(
  sourceData: GraphData | PartnersNameWithGraphColor,
  selectedValues: string[]
): GraphData {
  return Object.keys(sourceData)
    .filter(
      key => key === 'date' || selectedValues.includes(key.split('Color')[0])
    )
    .reduce(
      (obj, key) => {
        obj[key] = sourceData[key];
        return obj;
      },
      { date: null }
    );
}

function createComboBoxItems(partnersNameWithGraphColor): ComboBoxItem[] {
  return Object.keys(partnersNameWithGraphColor).map(partnerName => ({
    name: partnerName,
    value: partnerName,
  }));
}

function prepareDataForRegionGraphs(
  dataEntry: OrderDateByAccountCount,
  partnersColor: PartnersNameWithGraphColor
): GraphData {
  const valueObject: GraphData = { date: null };
  dataEntry.counts.forEach(count => {
    if (valueObject.hasOwnProperty(count.accountName)) {
      valueObject[count.accountName] += count.count;
    } else {
      valueObject[count.accountName] = count.count;
      valueObject[`${count.accountName}Color`] =
        partnersColor[count.accountName];
      valueObject.date = dayjs(count.date).format('YYYY-MM-DD');
    }
  });
  return valueObject;
}

function sortDates(dateEntry: GraphData[]) {
  return dateEntry.sort((a, b) => {
    return dayjs(a.date).isBefore(dayjs(b.date))
      ? -1
      : dayjs(a.date).isAfter(dayjs(b.date))
      ? 1
      : 0;
  });
}

function serializeDataForTotalGraph(
  fetchResult: VolumesReportInterface,
  setVolumesByDate: Dispatch<VolumeSerializedValues>,
  setPartnersNameWithGraphColor: Dispatch<PartnersNameWithGraphColor>
) {
  const serializedData: VolumeSerializedValues = {
    TOTAL: [],
  };
  const partnersColor = {};
  const { dateCounts, regionCounts } = fetchResult;
  dateCounts.forEach(reportEntry => {
    reportEntry.counts.forEach(count => {
      if (!partnersColor.hasOwnProperty(count.accountName)) {
        partnersColor[count.accountName] = '';
      }
    });
  });
  Object.keys(partnersColor).forEach(
    (key, ix) => (partnersColor[key] = kelly_colors[ix % kelly_colors.length])
  );
  serializedData.TOTAL = sortDates(
    dateCounts.map(dataEntry =>
      prepareDataForRegionGraphs(dataEntry, partnersColor)
    )
  );
  regionCounts.forEach(
    data =>
      (serializedData[data.region.split('_')[0]] = sortDates(
        data.orderDateByAccountCount.map(orderByDateData =>
          prepareDataForRegionGraphs(orderByDateData, partnersColor)
        )
      ))
  );
  console.log(serializedData);
  setVolumesByDate(serializedData);
  setPartnersNameWithGraphColor(partnersColor);
}

function createGraphForRegion(
  graphData: GraphData[],
  filteredPartnersNameWithGraphColor: PartnersNameWithGraphColor
): JSX.Element {
  console.log('graph', graphData);
  return (
    <div className="graph-container">
      <ResponsiveBar
        data={graphData}
        keys={Object.keys(filteredPartnersNameWithGraphColor)}
        indexBy="date"
        margin={{ top: 50, right: 10, bottom: 150, left: 50 }}
        padding={0.3}
        axisTop={null}
        axisRight={null}
        colors={chartData => {
          const { id, data } = chartData;
          return data[`${id}Color`];
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -45,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'delivered orders',
          legendPosition: 'middle',
          legendOffset: -40,
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={data => {
          return invertColor(
            kelly_colors.find(color => color === data.color),
            true
          );
        }}
        layers={['grid', 'axes', 'bars', 'markers'] as Layer[]}
        legends={[
          {
            dataFrom: 'keys',
            anchor: 'bottom',
            direction: 'column',
            justify: false,
            translateX: 0,
            translateY: 100,
            itemsSpacing: 2,
            itemWidth: 120,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 20,
            effects: [
              {
                on: 'hover',
                style: {
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
        animate={true}
        motionStiffness={90}
        motionDamping={15}
      />
      <div className="custom-legend">
        {Object.keys(filteredPartnersNameWithGraphColor).map(partner => {
          return (
            <span key={partner}>
              <svg width={10} height={10}>
                <rect
                  fill={filteredPartnersNameWithGraphColor[partner]}
                  stroke={filteredPartnersNameWithGraphColor[partner]}
                  width={10}
                  height={10}
                  style={{ pointerEvents: 'none' }}
                />
              </svg>{' '}
              {partner}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function createTabs(
  filteredVolumesByRegion: VolumeSerializedValues,
  filteredPartnersNameWithGraphColor: PartnersNameWithGraphColor
) {
  return Object.keys(filteredVolumesByRegion)
    .sort((a, b) => sortRegionsForView(a, b))
    .map((regionKey, ix) => {
      return {
        tabClassName: 'volume-report-tab',
        label: regionKey,
        tabValue: ix,
        tabPanelNode: createGraphForRegion(
          filteredVolumesByRegion[regionKey],
          filteredPartnersNameWithGraphColor
        ),
      };
    });
}
export const VolumesReport: FunctionComponent = () => {
  const [serializedVolumesByRegion, setVolumesByDate] =
    useState<VolumeSerializedValues>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [comboBoxValues, setComboBoxValues] = useState<string[]>([]);
  const [isSelectAllChecked, toggleSelectAllChecked] = useState<boolean>(true);
  const [partnersNameWithGraphColor, setPartnersNameWithGraphColor] =
    useState<PartnersNameWithGraphColor>(null);
  const [date, setDate] = useState<Date[]>([
    dayjs().subtract(30, 'day').toDate(),
    dayjs().toDate(),
  ]);

  const getVolumesDataCallback = useCallback(async () => {
    const [startDate, endDate] = date;
    serializeDataForTotalGraph(
      await fetchDeliveriesByAccount(startDate, endDate),
      setVolumesByDate,
      setPartnersNameWithGraphColor
    );
  }, [date]);

  useEffect(() => {
    getVolumesDataCallback()
      .then(() => console.log('fetched'))
      .catch();
  }, [getVolumesDataCallback]);

  if (serializedVolumesByRegion && partnersNameWithGraphColor) {
    const comboBoxItems = createComboBoxItems(partnersNameWithGraphColor);
    let filteredVolumesByRegion: VolumeSerializedValues =
      serializedVolumesByRegion;
    let filteredPartnersNameWithGraphColor = partnersNameWithGraphColor;
    if (comboBoxValues.length && !isSelectAllChecked) {
      filteredVolumesByRegion = { TOTAL: [] };
      filteredPartnersNameWithGraphColor = filterPartnerColorBySelected(
        partnersNameWithGraphColor,
        comboBoxValues
      );
      Object.keys(serializedVolumesByRegion).forEach(regionKey => {
        filteredVolumesByRegion[regionKey] = serializedVolumesByRegion[
          regionKey
        ].map(valuesByDate =>
          filterPartnerBySelected(valuesByDate, comboBoxValues)
        );
      });
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    const onChange = (event: ChangeEvent<{}>, value: number): void => {
      setSelectedTab(value);
    };

    return (
      <div className="graph-container">
        <Helmet>
          <title>Reports - Volumes</title>
        </Helmet>
        <Flatpickr
          onChange={(selectedDates, representation, instance) =>
            doCheckSelectedPeriod(selectedDates, instance)
          }
          onClose={selectedDates => setDate(selectedDates)}
          options={{
            dateFormat: 'l d M',
            defaultDate: date,
            mode: 'range',
            showMonths: 1,
            shorthandCurrentMonth: true,
            locale: { firstDayOfWeek: 1 },
          }}
          render={(props, ref) => (
            <input className="date-picker-container" ref={ref} />
          )}
        />
        <ComboBoxComponent
          className="volume-report-combobox"
          comboBoxName={`Volume report selected partners`}
          id={`volume-partners-selection`}
          disabled={isSelectAllChecked}
          multiple
          comboBoxItems={comboBoxItems}
          onChange={(ev, arrayOfObjectValues: ComboBoxItem | ComboBoxItem[]) =>
            setComboBoxValues(
              // FIXME: Remove casting
              (arrayOfObjectValues as ComboBoxItem[]).map(
                selected => selected.value
              )
            )
          }
        />
        <CheckboxComponent
          checked={isSelectAllChecked}
          handleChange={event => toggleSelectAllChecked(event.target.checked)}
          label="Show volume data for all partners"
          name="show-all-checkbox"
        />
        <TabsComponent
          onChange={onChange}
          tabsChildren={createTabs(
            filteredVolumesByRegion,
            filteredPartnersNameWithGraphColor
          )}
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
        />
      </div>
    );
  } else {
    return (
      <div className="loader-container">
        <Loader size="xxl" type={'spinner'} />
      </div>
    );
  }
};
